<template>
  <div class="doc-section">
    <div class="section-header">
      <span class="material-icons-sharp section-icon">trending_up</span>
      <h2>Statistiques &amp; Rapports</h2>
    </div>

    <p class="section-intro">
      Le module Statistiques génère des rapports détaillés sur l'activité de l'entreprise :
      ventes, approvisionnements, stocks, finances. Les données sont filtrables et exportables.
    </p>

    <!-- ===== PARAMÈTRES ===== -->
    <h3 class="sub-title">Paramètres du rapport</h3>
    <p class="section-text">
      Le formulaire de paramétrage possède de nombreux filtres. Les champs affichés dépendent du <em>Type de statistique</em> sélectionné.
    </p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Type de statistique</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Le type de rapport souhaité (Ventes, Approvisionnement, Stock, Finance, etc.).</td></tr>
          <tr><td><strong>Produit</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par produit (affiché selon le type).</td></tr>
          <tr><td><strong>Taxe</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par type de taxe.</td></tr>
          <tr><td><strong>Transitaire</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par transitaire.</td></tr>
          <tr><td><strong>Navire</strong></td><td>Texte</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par nom de navire.</td></tr>
          <tr><td><strong>Fournisseur</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par fournisseur.</td></tr>
          <tr><td><strong>Client</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par client.</td></tr>
          <tr><td><strong>Magasin</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par magasin.</td></tr>
          <tr><td><strong>BL</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par connaissement.</td></tr>
          <tr><td><strong>Facture</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par facture.</td></tr>
          <tr><td><strong>Pays</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par pays d'origine.</td></tr>
          <tr><td><strong>Type de compte</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par type de compte (Espèces, Chèque, Virement).</td></tr>
          <tr><td><strong>Mode de paiement</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Filtrer par méthode de paiement.</td></tr>
          <tr><td><strong>Date début</strong></td><td>Date</td><td><span class="required-badge badge-yes">Oui</span></td><td>Début de la période d'analyse.</td></tr>
          <tr><td><strong>Date fin</strong></td><td>Date</td><td><span class="required-badge badge-yes">Oui</span></td><td>Fin de la période d'analyse.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Procédure : Générer un rapport</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Ouvrez le module <strong>Statistiques</strong> depuis le menu latéral.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Sélectionnez le <em>Type de statistique</em>. Les filtres pertinents s'affichent automatiquement.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Remplissez les filtres souhaités (produit, fournisseur, client, magasin…).</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Sélectionnez la période : <em>Date début</em> et <em>Date fin</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">5</div>
        <p>Cliquez sur <span class="btn-ref">Charger les données</span>. Le rapport s'affiche sous forme de tableaux et/ou graphiques.</p>
      </div>
    </div>

    <!-- ===== EXPORT ===== -->
    <h3 class="sub-title">Exporter les données</h3>
    <p class="section-text">
      Une fois le rapport généré, plusieurs options d'export sont disponibles :
    </p>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Bouton</th><th>Action</th></tr>
        </thead>
        <tbody>
          <tr><td><span class="btn-ref">Télécharger le rapport</span></td><td>Génère un fichier PDF du rapport affiché.</td></tr>
          <tr><td><span class="btn-ref">Exportation des données</span></td><td>Lance un export des données brutes.</td></tr>
          <tr><td><span class="btn-ref">Exporter EXCEL</span></td><td>Télécharge un fichier Excel (.xlsx) avec toutes les données du rapport.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Procédure : Exporter en Excel</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Générez d'abord le rapport (étapes ci-dessus).</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Cliquez sur <span class="btn-ref">Exporter EXCEL</span>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Le fichier est téléchargé automatiquement dans votre dossier de téléchargements.</p>
      </div>
    </div>

    <div class="info-card tip">
      <div class="card-title">
        <span class="material-icons-sharp" style="font-size:16px;color:#4caf50">lightbulb</span>
        Astuce
      </div>
      <p>
        Pour des rapports réguliers, gardez les mêmes filtres et changez uniquement les dates.
        Les graphiques s'ajustent automatiquement à la nouvelle période.
      </p>
    </div>
  </div>
</template>
<script>
export default { name: "DocStatistiques" };
</script>
